import React, { useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { useField } from 'formik'
import {
  LedgerAccountFragment,
  LedgerAccountResourceForContactFragment,
  LegalEntityForFinancialDetailFragment,
} from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import {
  LedgerAccountSelector,
  formatAccountName,
} from 'modules/banking/components/LedgerAccountSelector'

const defaultAccount = (legalEntityId: string): LedgerAccountFragment => ({
  id: '0',
  name: '',
  legalEntityId: legalEntityId,
  number: '',
  type: 'non_current_asset',
  currency: 'EUR',
  disabled: false,
})

interface LedgerAccountSelectorItemProps {
  formFieldName: string
  legalEntities: LegalEntityForFinancialDetailFragment[]
  submitting: boolean
  canManage: boolean
  seeSalesLedgerAccounts: boolean
  seePurchaseLedgerAccounts: boolean
}

const LedgerAccountSelectorItem: React.FC<LedgerAccountSelectorItemProps> = ({
  formFieldName,
  legalEntities,
  submitting,
  canManage,
  seeSalesLedgerAccounts,
  seePurchaseLedgerAccounts,
}) => {
  const [_, { value: accountResourceForLegalEntity }, { setValue }] = useField<{
    legalEntityId: string
    accountResources: LedgerAccountResourceForContactFragment[]
  }>(formFieldName)

  const { company } = useCurrentSession()

  const legalEntityId = accountResourceForLegalEntity.legalEntityId || legalEntities[0]?.id
  const selectedLegalEntity = useMemo(
    () => legalEntities.find((le) => le.id === legalEntityId),
    [legalEntities, legalEntityId]
  )

  const getAccountId = (operationType: string) =>
    accountResourceForLegalEntity.accountResources.find(
      (ar) =>
        ar.operationType === operationType ||
        (ar.operationType === null && operationType === 'purchase')
    )?.account.id ?? '-1'

  const purchaseLedgerAccount = selectedLegalEntity?.financeAccounts.find(
    (a) => a.id === getAccountId('purchase')
  )
  const saleLedgerAccount = selectedLegalEntity?.financeAccounts.find(
    (a) => a.id === getAccountId('sale')
  )

  const handleOnchangeLedgerAccount = (newLedgerAccountId: string, operationType: string) => {
    const newLedgerAccountIdNum = String(newLedgerAccountId)
    const financeAccount =
      selectedLegalEntity?.financeAccounts.find((fa) => fa.id === newLedgerAccountIdNum) ??
      defaultAccount(
        selectedLegalEntity?.id ?? company.legalEntities.find((el) => el.main)?.id ?? '0'
      )

    setValue({
      ...accountResourceForLegalEntity,
      accountResources: accountResourceForLegalEntity.accountResources.map((ar) =>
        ar.operationType === operationType ||
        (ar.operationType === null && operationType === 'purchase')
          ? { ...ar, account: financeAccount }
          : ar
      ),
    })
  }

  const handleOnchangeLegalEntity = (value: string) => {
    const newLegalEntityId = String(value)
    setValue({
      legalEntityId: newLegalEntityId,
      accountResources: accountResourceForLegalEntity.accountResources.map((ar) => ({
        ...ar,
        account: defaultAccount(newLegalEntityId),
      })),
    })
  }

  const legalEntityOptions = legalEntities.map((le) => ({
    label: le.legalName ?? '',
    value: le.id,
  }))

  const bothSelectors = seeSalesLedgerAccounts && seePurchaseLedgerAccounts

  return (
    <Box flexDirection="row" gap="s16" width="full">
      <Box maxWidth="s200" minWidth="s200">
        <Select
          label={i18n.t('finance.workspace.legal_entity_scope.selector_label')}
          options={legalEntityOptions}
          onChange={handleOnchangeLegalEntity}
          value={String(legalEntityId)}
          none={i18n.t('common.select')}
          readonly={
            submitting ||
            accountResourceForLegalEntity.accountResources.some((ar) => ar.id && ar.id !== '-1')
          }
        />
      </Box>
      <Box width="full" minWidth="s200">
        {selectedLegalEntity && (
          <Box width="full" flexDirection="row" gap="s8">
            {seePurchaseLedgerAccounts && (
              <Box width="full">
                <LedgerAccountSelector
                  legalEntityId={selectedLegalEntity.id}
                  label={
                    bothSelectors
                      ? i18n.t('finance.contacts.contact_details.ledger_account.purchase_account')
                      : i18n.t('finance.contacts.contact_details.ledger_account.title')
                  }
                  currentLedgerAccountName={formatAccountName(purchaseLedgerAccount)}
                  accounts={selectedLegalEntity?.financeAccounts}
                  canManage={canManage}
                  disabled={submitting}
                  onSelect={(id) => handleOnchangeLedgerAccount(String(id), 'purchase')}
                />
              </Box>
            )}
            {seeSalesLedgerAccounts && (
              <Box width="full">
                <LedgerAccountSelector
                  legalEntityId={selectedLegalEntity.id}
                  label={
                    bothSelectors
                      ? i18n.t('finance.contacts.contact_details.ledger_account.sale_account')
                      : i18n.t('finance.contacts.contact_details.ledger_account.title')
                  }
                  currentLedgerAccountName={formatAccountName(saleLedgerAccount)}
                  accounts={selectedLegalEntity?.financeAccounts}
                  canManage={canManage}
                  disabled={submitting}
                  onSelect={(id) => handleOnchangeLedgerAccount(String(id), 'sale')}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default LedgerAccountSelectorItem
